import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as groupsApi from '@/api/groups';
import * as usersApi from '@/api/users';
import type { SystemUserDetail } from '@/api/users';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import { consoleGroupActions } from '../userLinks';

interface Props {
  user: SystemUserDetail;
  onChanged: () => void;
}

export function GroupsTab({ user, onChanged }: Props) {
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const groupsQuery = useQuery({ queryKey: ['groups', 'all'], queryFn: () => groupsApi.list({}) });
  const [selected, setSelected] = useState<number[]>(user.groups.map((g) => g.id));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (ids: number[]) => usersApi.setGroups(user.id, ids),
    onSuccess: () => {
      snackbar.show('Group membership updated.', 'success');
      onChanged();
    },
    onError: (e: unknown) =>
      setError(e instanceof ConsoleApiError ? e.message : 'Could not update groups.'),
  });

  if (groupsQuery.isLoading) {
    return <CircularProgress />;
  }
  if (groupsQuery.isError) {
    return <Alert severity="error">Couldn’t load groups.</Alert>;
  }

  const dirty =
    selected.length !== user.groups.length ||
    selected.some((id) => !user.groups.find((g) => g.id === id));

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Stack spacing={2}>
        <Box>
          <strong>Current memberships:</strong>{' '}
          {user.groups.length ? (
            user.groups.map((g) => (
              <ValueActions key={g.id} chip actions={consoleGroupActions(g)} sx={{ mr: 0.5 }}>
                <Chip label={g.name} />
              </ValueActions>
            ))
          ) : (
            <em>None</em>
          )}
        </Box>
        <FormControl fullWidth disabled={!hasPerm('console_perm_grant')}>
          <InputLabel id="groups-label">Groups</InputLabel>
          <Select
            multiple
            labelId="groups-label"
            label="Groups"
            value={selected}
            onChange={(e) =>
              setSelected(
                typeof e.target.value === 'string'
                  ? e.target.value.split(',').map(Number)
                  : (e.target.value as number[]),
              )
            }
            renderValue={(ids) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(ids as number[]).map((id) => {
                  const g = groupsQuery.data?.results.find((x) => x.id === id);
                  return <Chip key={id} label={g?.name ?? `#${id}`} size="small" />;
                })}
              </Box>
            )}
          >
            {groupsQuery.data?.results.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button
            variant="contained"
            disabled={!dirty || mutation.isPending || !hasPerm('console_perm_grant')}
            onClick={() => {
              setError(null);
              mutation.mutate(selected);
            }}
          >
            Save changes
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
